"use client";

import { c as _c } from "react/compiler-runtime";
import { Tooltip } from "react-tooltip";
import React, { useMemo } from "react";
import type { DropPrivileges } from "@/hooks/useDropPriviledges";
import { ChatRestriction, SubmissionRestriction } from "@/hooks/useDropPriviledges";
interface CreateDropDropModeToggleProps {
  readonly isDropMode: boolean;
  readonly onDropModeChange: (isDropMode: boolean) => void;
  readonly privileges: DropPrivileges;
  readonly exitLabel?: string | null;
}
export const CreateDropDropModeToggle: React.FC<CreateDropDropModeToggleProps> = t0 => {
  const $ = _c(27);
  const {
    isDropMode,
    onDropModeChange,
    privileges,
    exitLabel: t1
  } = t0;
  const exitLabel = t1 === undefined ? null : t1;
  const {
    chatRestriction,
    submissionRestriction
  } = privileges;
  const hasExitLabel = exitLabel !== null;
  const hasChatRestriction = chatRestriction !== null;
  const hasSubmissionRestriction = submissionRestriction !== null;
  const isExitOnly = isDropMode && hasExitLabel;
  const isDisabled = !isExitOnly && (isDropMode && hasChatRestriction || !isDropMode && hasSubmissionRestriction);
  const canToggle = isExitOnly || (isDropMode ? !hasChatRestriction : !hasSubmissionRestriction);
  const targetRestriction = isDropMode ? chatRestriction : submissionRestriction;
  let t2;
  if ($[0] !== canToggle || $[1] !== isDropMode || $[2] !== isExitOnly || $[3] !== onDropModeChange) {
    t2 = canToggle ? () => onDropModeChange(isExitOnly ? false : !isDropMode) : undefined;
    $[0] = canToggle;
    $[1] = isDropMode;
    $[2] = isExitOnly;
    $[3] = onDropModeChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleToggleClick = t2;
  let t3;
  bb0: {
    if (isDisabled) {
      t3 = `${"tw-group tw-relative tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-transparent tw-text-sm tw-transition-all active:tw-scale-95 md:tw-h-10 md:tw-w-10 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2"} tw-opacity-50 tw-cursor-not-allowed ${isDropMode ? "tw-bg-primary-600 tw-text-white" : "tw-bg-white/[0.06] tw-text-iron-500"}`;
      break bb0;
    }
    if (isDropMode) {
      t3 = "tw-group tw-relative tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-transparent tw-text-sm tw-transition-all active:tw-scale-95 md:tw-h-10 md:tw-w-10 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 tw-bg-primary-600 tw-text-white desktop-hover:hover:tw-bg-primary-500 focus-visible:tw-outline-primary-500";
      break bb0;
    }
    t3 = "tw-group tw-relative tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-transparent tw-text-sm tw-transition-all active:tw-scale-95 md:tw-h-10 md:tw-w-10 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 tw-bg-white/[0.06] tw-text-iron-300 desktop-hover:hover:tw-bg-white/[0.12] desktop-hover:hover:tw-text-white focus-visible:tw-outline-iron-500";
  }
  const buttonClassName = t3;
  const getRestrictionMessage = _temp;
  let t4;
  bb1: {
    if (isExitOnly) {
      let t5;
      if ($[5] !== exitLabel) {
        t5 = <span className="tw-text-xs">{exitLabel}</span>;
        $[5] = exitLabel;
        $[6] = t5;
      } else {
        t5 = $[6];
      }
      t4 = t5;
      break bb1;
    }
    if (!isDisabled && canToggle) {
      const t5 = isDropMode ? "Chat" : "Drop";
      let t6;
      if ($[7] !== t5) {
        t6 = <span className="tw-text-xs">Switch to {t5} Mode</span>;
        $[7] = t5;
        $[8] = t6;
      } else {
        t6 = $[8];
      }
      t4 = t6;
      break bb1;
    }
    if (targetRestriction !== null) {
      const t5 = getRestrictionMessage(targetRestriction, !isDropMode);
      let t6;
      if ($[9] !== t5) {
        t6 = <span className="tw-text-xs">{t5}</span>;
        $[9] = t5;
        $[10] = t6;
      } else {
        t6 = $[10];
      }
      t4 = t6;
      break bb1;
    }
    const t5 = isDropMode ? "Chat" : "Drop";
    let t6;
    if ($[11] !== t5) {
      t6 = <span className="tw-text-xs">{t5} mode is unavailable</span>;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t4 = t6;
  }
  const tooltipContent = t4;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <svg className="tw-h-4.5 tw-w-4.5 tw-flex-shrink-0 tw-transition-colors md:tw-h-5 md:tw-w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z" /><path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97" /></svg>;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== isDropMode) {
    t6 = !isDropMode && <span className="tw-absolute tw-right-1 tw-top-1 tw-flex tw-h-2.5 tw-w-2.5 tw-items-center tw-justify-center tw-rounded-full tw-bg-iron-950"><span className="tw-text-[9px] tw-font-black tw-leading-none tw-text-primary-400">+</span></span>;
    $[14] = isDropMode;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== buttonClassName || $[17] !== handleToggleClick || $[18] !== isDisabled || $[19] !== t6) {
    t7 = <div><button type="button" onClick={handleToggleClick} disabled={isDisabled} className={buttonClassName} data-tooltip-id="drop-mode-toggle">{t5}{t6}</button></div>;
    $[16] = buttonClassName;
    $[17] = handleToggleClick;
    $[18] = isDisabled;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      backgroundColor: "#1F2937",
      color: "white",
      padding: "4px 8px"
    };
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== tooltipContent) {
    t9 = <Tooltip id="drop-mode-toggle" style={t8}>{tooltipContent}</Tooltip>;
    $[22] = tooltipContent;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t7 || $[25] !== t9) {
    t10 = <>{t7}{t9}</>;
    $[24] = t7;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
};
function _temp(restriction, isChat) {
  switch (restriction) {
    case ChatRestriction.NOT_LOGGED_IN:
    case SubmissionRestriction.NOT_LOGGED_IN:
      {
        return `Please log in to ${isChat ? "drop" : "chat"}`;
      }
    case ChatRestriction.PROXY_USER:
    case SubmissionRestriction.PROXY_USER:
      {
        return `Proxy users cannot ${isChat ? "drop" : "chat"}`;
      }
    case ChatRestriction.NO_PERMISSION:
    case SubmissionRestriction.NO_PERMISSION:
      {
        return `You don't have permission to ${isChat ? "drop" : "chat"}`;
      }
    case ChatRestriction.DISABLED:
      {
        return "Chat is currently disabled";
      }
    case SubmissionRestriction.NOT_STARTED:
      {
        return "Drop submissions haven't started yet";
      }
    case SubmissionRestriction.ENDED:
      {
        return "Drop submissions have ended";
      }
    case SubmissionRestriction.MAX_DROPS_REACHED:
      {
        return "You have reached the maximum number of drops allowed";
      }
    default:
      {
        return `${isChat ? "Drop" : "Chat"} mode is unavailable`;
      }
  }
}